import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FilterField, FilterOperator, FindOptions, NotificationDTO } from '@app/models';
import { NotificationService } from '@app/shared/services';
import { utils } from '@app/shared/utils';
import { ToastService } from '@teorema/patron';

@Component({
  selector: 'patron-sending-form',
  templateUrl: './sending-form.component.html',
  styleUrls: ['./sending-form.component.css'],
  standalone: false,
})
export class SendingFormComponent implements OnInit, AfterViewInit, OnDestroy {
  formGroup!: FormGroup;
  loading = false;
  receiversSelectFindOptions: FindOptions = {
    filters: [],
  };
  intervals: any[] = [];

  platform_options = [
    { value: 'ios', label: 'IOS' },
    { value: 'android', label: 'Android' },
  ];

  modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'], // toggled buttons
      ['blockquote', 'code-block'],

      //[{ 'header': 1 }, { 'header': 2 }],               // custom button values
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
      [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
      [{ direction: 'rtl' }], // text direction

      [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
      [{ header: [1, 2, 3, 4, 5, 6, false] }],

      [{ color: [] }, { background: [] }], // dropdown with defaults from theme
      [{ font: [] }],
      [{ align: [] }],

      ['clean'], // remove formatting button

      ['link' /*, 'image', 'video'*/], // link and image, video*/
    ],
  };

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      title: [null, Validators.maxLength(255)],
      message: [null, Validators.required],
      receivers: [null, Validators.required],
      systems: [null, Validators.required],
      platforms: [null],
      force_update: [null],
    });

    if (!utils.isUser() && !utils.isSupport()) {
      this.receiversSelectFindOptions.filters?.push(
        new FilterField({
          name: 'is_user',
          label: '',
          operator: FilterOperator.has,
          value: 1,
        })
      );
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.translateQuill();
    }, 2000);
  }

  async onSubmit() {
    if (this.formGroup?.invalid) {
      this.toastService.show({
        color: 'error',
        message: 'Dados obrigatórios!',
      });
      return;
    }

    let message = this.formGroup.get('message')?.value;
    let msgByteSize = new Blob([message]).size;

    if (msgByteSize > 4000) {
      this.toastService.show({
        color: 'error',
        message: 'Mensagem muito longa!',
      });
      return;
    }

    let systemsValue = this.formGroup.get('systems')?.value;

    systemsValue = [systemsValue.id];

    let selectedPlatforms = JSON.stringify(this.formGroup.get('platforms')?.value || []);
    let forceUpdate = JSON.stringify(this.formGroup.get('force_update')?.value ?? 0);

    let notification = {
      title: this.formGroup.get('title')?.value,
      message: message,
      receivers: this.formGroup.get('receivers')?.value.map((bwUser: any) => {
        return {
          bw_user_id: bwUser.id,
          company_group_id: utils.getCompanyGroup()!.id,
        };
      }),
      systems: systemsValue,
      platforms: selectedPlatforms,
      force_update: forceUpdate,
    };

    if (!notification.title) {
      notification.title = notification.message.replace(/<[^>]*>/g, '');
    }

    if (notification.title.length > 100) {
      notification.title = notification.title.substring(0, 100);
    }

    this.loading = true;
    const response: any = await this.notificationService
      .save(notification as unknown as NotificationDTO)
      .finally(() => {
        this.loading = false;
      })
      .catch(() => {
        this.toastService.show({
          color: 'error',
          message: `Houve um erro ao enviar a notificação`,
        });
      });

    if (response?.id) {
      this.toastService.show({ color: 'success', message: `Notificação enviada com sucesso` });
      this.formGroup.reset();
      this.notificationService.sentEventEmitter.emit(response!.id);
    } else if (response?.message) {
      this.toastService.show({ color: 'warn', message: response?.message });
    }
  }

  translateQuill() {
    this.translatePseudoElements('ql-picker-item', ':before', {
      '"Small"': 'quill-content-small',
      '"Large"': 'quill-content-large',
      '"Huge"': 'quill-content-huge',

      '"Heading 1"': 'quill-content-h1',
      '"Heading 2"': 'quill-content-h2',
      '"Heading 3"': 'quill-content-h3',
      '"Heading 4"': 'quill-content-h4',
      '"Heading 5"': 'quill-content-h5',
      '"Heading 6"': 'quill-content-h6',
    });

    this.translatePseudoElements('ql-editor ql-blank', ':before', {
      '"Insert text here ..."': 'quill-content-insert-text-here',
    });

    this.intervals.push(
      setInterval(() => {
        this.translatePseudoElements('ql-tooltip ql-editing', ':before', {
          '"Enter link:"': 'quill-content-enter-link',
        });

        this.translatePseudoElements('ql-action', ':after', {
          '"Save"': 'quill-content-save',
        });
      }, 1000)
    );
  }

  translatePseudoElements(classes: string, pseudoElement: string, wordsToTranslate: any) {
    let elements = Array.from(document.getElementsByClassName(classes));

    elements.forEach((element: any) => {
      var styles = window.getComputedStyle(element, pseudoElement);
      var content = styles['content'];

      if (content) {
        let clazz = wordsToTranslate[content];

        if (clazz && !element.classList.contains(clazz)) {
          element.classList.add(clazz);
        }
      }
      if (content === '"Save"') {
        element.classList.add('quill-content-save');
      }
    });
  }

  ngOnDestroy(): void {
    this.intervals.forEach((interval: any) => {
      clearInterval(interval);
    });
  }
}
